import type { DisplayMode } from '@/types/book';

export type PageTransitionFrame =
  | { type: 'idle'; pageIndex: number }
  | {
      type: 'fold';
      fromPageIndex: number;
      targetPageIndex: number;
      direction: 'forward' | 'back';
      progress: number;
    };

/** 将明确的起止页和进度解析为与播放方向无关的确定帧。 */
export function resolvePageTransitionFrame(
  fromPageIndex: number,
  targetPageIndex: number,
  progress: number
): PageTransitionFrame {
  const clamped = Math.max(0, Math.min(1, progress));

  if (fromPageIndex === targetPageIndex || clamped <= 0) {
    return { type: 'idle', pageIndex: fromPageIndex };
  }
  if (clamped >= 1) {
    return { type: 'idle', pageIndex: targetPageIndex };
  }

  return {
    type: 'fold',
    fromPageIndex,
    targetPageIndex,
    direction: targetPageIndex > fromPageIndex ? 'forward' : 'back',
    progress: clamped
  };
}

/**
 * 判断一帧翻页是否应该影响当前渲染状态。
 * 步骤1：只接受当前页处在 from/target 两端之一的帧，避免时间轴中未来或过期的 tween 覆盖页码。
 */
export function shouldRenderPageTransitionFrame(
  currentPageIndex: number,
  fromPageIndex: number,
  targetPageIndex: number,
  _progress: number
): boolean {
  return currentPageIndex === fromPageIndex || currentPageIndex === targetPageIndex;
}

/** 双页封面模型的初始跨页左页索引。 */
const DOUBLE_INITIAL_PAGE_INDEX = -1;

/**
 * 将页面索引归一化为跨页左页索引（与 PixiBookRenderer.normalizeTargetSpreadIndex 一致）。
 */
export function normalizeSpreadPageIndex(
  pageIndex: number,
  displayMode: DisplayMode,
  pageCount: number
): number {
  if (displayMode === 'single') return pageIndex;
  if (pageIndex <= 0) return DOUBLE_INITIAL_PAGE_INDEX;
  if (pageIndex === pageCount - 1 && pageCount % 2 === 0) return pageIndex;
  return pageIndex % 2 === 0 ? pageIndex - 1 : pageIndex;
}

/**
 * 判断 from → to 是否跨越了一个真实跨页（双页模式下同跨页内切换不播放翻页音效）。
 */
export function isSpreadFlipTransition(
  fromPageIndex: number,
  toPageIndex: number,
  displayMode: DisplayMode,
  pageCount: number
): boolean {
  if (fromPageIndex === toPageIndex) return false;
  if (displayMode === 'single') return true;

  return (
    normalizeSpreadPageIndex(fromPageIndex, displayMode, pageCount) !==
    normalizeSpreadPageIndex(toPageIndex, displayMode, pageCount)
  );
}
